import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Search, ClipboardCheck, Shield, GraduationCap, Users, Cpu, Layers, FileText,
  ArrowRight, CheckCircle, AlertCircle, Clock, Building, Globe, Factory, BarChart3
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { SEO } from "../components/SEO";
import CertificationFinder from "../components/tools/CertificationFinder";
import ReadinessAssessment from "../components/tools/ReadinessAssessment";
import ServiceRequestBuilder from "../components/tools/ServiceRequestBuilder";
import GapIdentifier from "../components/tools/GapIdentifier";
import RegulatoryChecker from "../components/tools/RegulatoryChecker";
import TrainingPathway from "../components/tools/TrainingPathway";
import RoleSkillsIdentifier from "../components/tools/RoleSkillsIdentifier";
import DigitalReadinessChecker from "../components/tools/DigitalReadinessChecker";
import IMSAnalyzer from "../components/tools/IMSAnalyzer";
import CompareResults from "../components/tools/CompareResults";
import ResourcesInsights from "../components/ResourcesInsights";

const Tools = () => {
  const [activeTool, setActiveTool] = useState(null);

  const tools = [
    {
      id: "certification-finder",
      name: "Certification Finder",
      icon: Search,
      description: "Discover which certifications and standards are right for your business based on industry, markets, and goals.",
      benefits: ["Personalized recommendations", "Export market guidance", "Priority ranking"],
      time: "3-5 minutes",
      color: "bg-blue-500"
    },
    {
      id: "readiness-assessment",
      name: "Compliance Readiness Assessment",
      icon: ClipboardCheck,
      description: "Evaluate your organization's readiness level for certification with a non-threatening self-assessment.",
      benefits: ["Readiness score", "Gap identification", "Timeline estimate"],
      time: "5-7 minutes",
      color: "bg-green-500"
    },
    {
      id: "gap-identifier",
      name: "Audit Gap Identifier",
      icon: AlertCircle,
      description: "Identify typical gaps and understand common root causes based on your audit findings.",
      benefits: ["Gap categorization", "Root cause insights", "Action priorities"],
      time: "5-10 minutes",
      color: "bg-amber-500"
    },
    {
      id: "regulatory-checker",
      name: "Regulatory Exposure Checker",
      icon: Shield,
      description: "Understand which regulations apply to your business based on location and export markets.",
      benefits: ["Regional regulations", "Export requirements", "Compliance mapping"],
      time: "3-5 minutes",
      color: "bg-purple-500"
    },
    {
      id: "training-pathway",
      name: "Training Pathway Builder",
      icon: GraduationCap,
      description: "Get personalized training recommendations based on your role, industry, and experience.",
      benefits: ["Role-based paths", "Skill development", "Course recommendations"],
      time: "3-5 minutes",
      color: "bg-indigo-500"
    },
    {
      id: "role-skills",
      name: "ISO Role-Based Skills Identifier",
      icon: Users,
      description: "Map organizational roles to ISO responsibilities and identify training needs.",
      benefits: ["Role mapping", "Competency gaps", "Training plans"],
      time: "5-7 minutes",
      color: "bg-pink-500"
    },
    {
      id: "digital-readiness",
      name: "AI & Digital Readiness Checker",
      icon: Cpu,
      description: "Evaluate your organization's readiness for AI, digital transformation, and cybersecurity.",
      benefits: ["Digital maturity", "AI awareness", "Security posture"],
      time: "5-7 minutes",
      color: "bg-cyan-500"
    },
    {
      id: "ims-analyzer",
      name: "IMS Opportunity Analyzer",
      icon: Layers,
      description: "Identify opportunities to integrate multiple management systems for efficiency.",
      benefits: ["Integration potential", "Cost savings", "Effort optimization"],
      time: "5-10 minutes",
      color: "bg-orange-500"
    }
  ];

  const renderTool = () => {
    switch (activeTool) {
      case "certification-finder":
        return <CertificationFinder onClose={() => setActiveTool(null)} />;
      case "readiness-assessment":
        return <ReadinessAssessment onClose={() => setActiveTool(null)} />;
      case "service-request":
        return <ServiceRequestBuilder onClose={() => setActiveTool(null)} />;
      case "gap-identifier":
        return <GapIdentifier onClose={() => setActiveTool(null)} />;
      case "regulatory-checker":
        return <RegulatoryChecker onClose={() => setActiveTool(null)} />;
      case "training-pathway":
        return <TrainingPathway onClose={() => setActiveTool(null)} />;
      case "role-skills":
        return <RoleSkillsIdentifier onClose={() => setActiveTool(null)} />;
      case "digital-readiness":
        return <DigitalReadinessChecker onClose={() => setActiveTool(null)} />;
      case "ims-analyzer":
        return <IMSAnalyzer onClose={() => setActiveTool(null)} />;
      case "compare-results":
        return <CompareResults onClose={() => setActiveTool(null)} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tools-page">
      <SEO
        title="Compliance Tools & Self-Assessments | CompLy CMS"
        description="Free interactive tools to help you identify certifications, assess compliance readiness, and plan your training. ISO 9001, ISO 22000, HACCP, FSSC 22000 guidance."
        url="/tools"
        keywords={["compliance tools", "certification finder", "ISO readiness assessment", "HACCP assessment", "food safety tools"]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-darker via-navy-dark to-navy text-white py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand/20 text-brand-light px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Search className="h-4 w-4" />
              Free Diagnostic Tools
            </div>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-6" data-testid="tools-title">
              Compliance Tools & Self-Assessments
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Use our interactive tools to identify the right certifications, assess your readiness, 
              and plan your compliance journey. No commitment required.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={() => setActiveTool("certification-finder")}
                className="bg-brand hover:bg-brand-dark text-white"
              >
                Find My Certification
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                onClick={() => setActiveTool("readiness-assessment")}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
              >
                Assess My Readiness
              </Button>
              <Button 
                onClick={() => setActiveTool("compare-results")}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
                data-testid="compare-results-btn"
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                Compare Results
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Active Tool Modal */}
      {activeTool && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {renderTool()}
          </div>
        </div>
      )}

      {/* Tools Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-primary mb-4">
              Choose Your Starting Point
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Each tool is designed to help you gain clarity on a specific aspect of your compliance journey. 
              Results can be combined for a complete picture.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tools.map((tool) => (
              <Card 
                key={tool.id}
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg ${
                  tool.comingSoon ? 'opacity-75' : 'cursor-pointer hover:-translate-y-1'
                }`}
                onClick={() => !tool.comingSoon && setActiveTool(tool.id)}
                data-testid={`tool-card-${tool.id}`}
              >
                {tool.comingSoon && (
                  <div className="absolute top-3 right-3">
                    <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className={`w-12 h-12 rounded-lg ${tool.color} flex items-center justify-center mb-3`}>
                    <tool.icon className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-lg">{tool.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="mb-4 min-h-[60px]">
                    {tool.description}
                  </CardDescription>
                  <div className="space-y-2 mb-4">
                    {tool.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="h-3 w-3 text-brand" />
                        {benefit}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {tool.time}
                    </span>
                    {!tool.comingSoon && (
                      <span className="text-brand text-sm font-medium flex items-center gap-1">
                        Start <ArrowRight className="h-3 w-3" />
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="bg-gradient-to-r from-brand to-brand-dark rounded-2xl p-8 lg:p-12 text-white text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-80" />
            <h2 className="font-heading font-bold text-2xl lg:text-3xl mb-4">
              Ready for Expert Support?
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto mb-6">
              Our diagnostic tools provide guidance, but nothing replaces expert consultation. 
              Submit a service request and let our team develop a tailored approach for your organization.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-brand hover:bg-slate-100"
              onClick={() => setActiveTool("service-request")}
            >
              Request Expert Support
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Resources & Insights Section */}
      <ResourcesInsights />
    </div>
  );
};

export default Tools;
